/**
 * User Hooks - React Query hooks for user operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services';
import type {
  ListUsersRequest,
  InviteUserRequest,
  UpdateUserRequest,
  UpdateProfileRequest,
} from '../types';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params?: ListUsersRequest) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
};

/**
 * Get current user profile
 */
export const useProfile = () => {
  return useQuery({
    queryKey: userKeys.profile(),
    queryFn: () => userService.getProfile(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Update current user profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => userService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update profile'));
    },
  });
};

/**
 * List users with optional filters
 */
export const useUsers = (params?: ListUsersRequest, enabled: boolean = true) => {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => userService.listUsers(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled,
  });
};

/**
 * Get user by ID
 */
export const useUser = (id: string) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
  });
};

/**
 * Invite new user
 */
export const useInviteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteUserRequest) => userService.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User invited successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to invite user'));
    },
  });
};

/**
 * Update user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userService.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
      toast.success('User updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update user'));
    },
  });
};

/**
 * Deactivate user (active -> inactive)
 */
export const useDeactivateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.deactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User deactivated');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to deactivate user')),
  });
};

/**
 * Reactivate user (inactive -> active)
 */
export const useReactivateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.reactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User reactivated');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to reactivate user')),
  });
};

/**
 * Soft delete user (inactive -> hidden)
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User deleted');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to delete user')),
  });
};

/**
 * Permanently delete a never-joined invited user
 */
export const useHardDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.hardDeleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User permanently deleted');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to permanently delete user')),
  });
};

/**
 * Restore a soft-deleted user
 */
export const useRestoreUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.restoreUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User restored');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to restore user')),
  });
};

/**
 * Resend invitation
 */
export const useResendInvitation = () => {
  return useMutation({
    mutationFn: (id: string) => userService.resendInvitation(id),
    onSuccess: () => {
      toast.success('Invitation resent successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to resend invitation'));
    },
  });
};
